import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as core from '../../../core'

export interface Request {
  // TODO: maybe generalize for other apps
  page?: Page
  sort?: Sort
  search?: Search
  filter?: Filter
}
export namespace Request {
  export const isRequest = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Request>({
      filter: TypeChecker.Factory.optional(Filter.isFilter),
      page: TypeChecker.Factory.optional(Page.isPage),
      search: TypeChecker.Factory.optional(Search.isSearch),
      sort: TypeChecker.Factory.optional(Sort.isSort),
    }),
  )

  export type ForCount = Omit<Request, 'page' | 'sort'>
}

export interface Page {
  skip?: number
  quantity?: number
}
export namespace Page {
  export const isPage = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Page>({
      quantity: TypeChecker.Factory.optional(TypeChecker.isNumber),
      skip: TypeChecker.Factory.optional(TypeChecker.isNumber),
    }),
  )
}

export interface Sort {
  attributeId: core.dms.models.Attribute.Id
  ascending?: boolean
}
export namespace Sort {
  export const isSort = TypeChecker.Factory.object<Sort>({
    ascending: TypeChecker.Factory.optional(TypeChecker.isBoolean),
    attributeId: core.dms.models.Attribute.Id.isId,
  })
}

export interface Search {
  term: string
}
export namespace Search {
  export const isSearch = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Search>({
      term: TypeChecker.isString,
    }),
  )
}

/* -------------------------------------------------------------------------- */
/*                                   Filters                                  */
/* -------------------------------------------------------------------------- */
// TODO: maybe generalize for other apps
export type Filter = Filter.Condition | Filter.Statement
export namespace Filter {
  export const isFilter: TypeChecker<Filter> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.union(Condition.isCondition, Statement.isStatement),
  )

  /**
   * Produces a {@link Filter} from a {@link Filter.ExplicitlyTyped}
   *
   * Inverse of {@link Filter.ExplicitlyTyped.from}
   */
  export function from({
    comparison,
    selection,
    statement,
  }: ExplicitlyTyped): Filter | undefined {
    return comparison ?? selection ?? statement
  }

  /**
   * Represents a {@link Filter} that does not need to be type-checked since the
   * type is conveyed through the property name
   *
   * If no properties are defined, there is no {@link Filter} (i.e. it is an
   * empty {@link Filter})
   */
  export type ExplicitlyTyped = AtMostOneOf<{
    readonly selection: Selection
    readonly comparison: Comparison
    readonly statement: Statement
  }>
  export namespace ExplicitlyTyped {
    /**
     * Produces a {@link Filter.ExplicitlyTyped} from a {@link Filter}
     *
     * Inverse of {@link Filter.from}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function from(filter: Filter | undefined): ExplicitlyTyped {
      if (!TypeChecker.isObject(filter)) {
        // Invalid type => empty filter
        return {}
      }

      // NOTE: we use the isProbably type checkers since we just need to know
      // which of these were intended. We don't care (here) if they are truly
      // correct
      if (Comparison.isProbablyComparison(filter)) {
        return { comparison: filter }
      }
      if (Selection.isProbablySelection(filter)) {
        return { selection: filter }
      }
      if (Statement.isProbablyStatement(filter)) {
        return { statement: filter }
      }

      // If it is not one of the above, it will be treated as an empty filter
      return {}
    }
  }

  export type Condition = Selection | Comparison
  export namespace Condition {
    export const isCondition: TypeChecker<Condition> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.union(
          Selection.isSelection,
          Comparison.isComparison,
        ),
    )
  }

  /* ------------------------------- Selections ------------------------------- */
  export interface Selection {
    attributeId: core.dms.models.Attribute.Id
    values: string[]
  }
  export namespace Selection {
    export const isSelection = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<Selection>({
        attributeId: core.dms.models.Attribute.Id.isId,
        values: TypeChecker.Factory.list(TypeChecker.isString),
      }),
    )
    /**
     * {@link isSelection} is very strict about every property being correct
     *
     * This just checks that enough properties are correct to indicate that the
     * given value was probably at least intended to be a {@link Selection}
     *
     * This can be useful when you are trying to narrow down what type of
     * {@link Filter} you have, and you'd rather deal with a partially incorrect
     * {@link Selection} than have to give up entirely
     */
    export const isProbablySelection = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<
        Replace.Properties<
          Replace.Properties<Selection, 'attributeId', string>,
          'values',
          readonly any[]
        >
      >({
        attributeId: TypeChecker.isString,
        values: TypeChecker.isReadonlyArray,
      }),
    ) as typeof isSelection
  }

  /* ------------------------------- Comparisons ------------------------------- */
  export interface Comparison {
    attributeId: core.dms.models.Attribute.Id
    operator: Comparison.Operator
    value: core.common.value.Raw
  }
  export namespace Comparison {
    export const isComparison = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<Comparison>({
        attributeId: core.dms.models.Attribute.Id.isId,
        operator: Operator.isOperator,
        value: core.common.value.Raw.isRaw,
      }),
    )
    /**
     * {@link isComparison} is very strict about every property being correct
     *
     * This just checks that enough properties are correct to indicate that the
     * given value was probably at least intended to be a {@link Comparison}
     *
     * This can be useful when you are trying to narrow down what type of
     * {@link Filter} you have, and you'd rather deal with a partially incorrect
     * {@link Comparison} than have to give up entirely
     */
    export const isProbablyComparison = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<Pick<Comparison, 'operator'>>({
        operator: Operator.isOperator,
      }),
    ) as typeof isComparison

    export enum Operator {
      Equals = '=',
      NotEquals = '!=',
      LessThan = '<',
      LessThanOrEqualTo = '<=',
      GreaterThan = '>',
      GreaterThanOrEqualTo = '>=',
      StartsWith = '_*',
      EndsWith = '*_',
      Contains = '*_*',
    }
    // Needs to be before namespace is defined
    const OPERATORS: ReadonlySet<Operator> = new Set(
      Object.values(Operator) as Operator[],
    )
    export namespace Operator {
      export const ALL: readonly Operator[] = Array.from(OPERATORS)
      export const isOperator = TypeChecker.Factory.oneOf(OPERATORS)
      export const assertIsOperator: TypeAsserter<Operator> =
        TypeAsserter.Factory.from(isOperator)
    }
  }

  /* ------------------------------- Statements ------------------------------- */
  // TODO: maybe use common logical structure
  export interface Statement {
    filters: Filter[]
    connector: Statement.Connector
  }
  export namespace Statement {
    export const isStatement = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<Statement>({
        connector: Connector.isConnector,
        filters: TypeChecker.Factory.list(Filter.isFilter),
      }),
    )
    /**
     * {@link isStatement} is very strict about every property being correct
     *
     * This just checks that enough properties are correct to indicate that the
     * given value was probably at least intended to be a {@link Statement}
     *
     * This can be useful when you are trying to narrow down what type of
     * {@link Filter} you have, and you'd rather deal with a partially incorrect
     * {@link Statement} than have to give up entirely
     */
    export const isProbablyStatement = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<
        Replace.Properties<Statement, 'filters', readonly any[]>
      >({
        connector: Connector.isConnector,
        filters: TypeChecker.isReadonlyArray,
      }),
    ) as typeof isStatement

    /**
     * Inverts every {@link Statement.Connector} in the given {@link Statement}
     * (including nested {@link Statement})
     *
     * @see {@link Statement.Connector.inverse}
     */
    export function invertConnector(statement: Readonly<Statement>): Statement {
      return {
        connector: Connector.inverse(statement.connector),
        filters: statement.filters.map(filter =>
          isProbablyStatement(filter) ? invertConnector(filter) : filter,
        ),
      } satisfies NoMissing<Statement>
    }

    /**
     * Determines how many filters in a {@link Statement} need to be met for the
     * entire {@link Statement} to be met
     */
    export enum Connector {
      And = 'AND',
      Or = 'OR',
    }
    // Needs to be before namespace is defined
    const CONNECTORS: ReadonlySet<Connector> = new Set(
      Object.values(Connector) as Connector[],
    )
    export namespace Connector {
      export const ALL: readonly Connector[] = Array.from(CONNECTORS)
      export const isConnector = TypeChecker.Factory.oneOf(CONNECTORS)
      export const assertIsConnector: TypeAsserter<Connector> =
        TypeAsserter.Factory.from(isConnector)

      /**
       * There are two {@link Connector}:
       * 1. {@link Connector.And}
       * 1. {@link Connector.Or}
       *
       * When you give this function one of those two, it will return the other
       */
      export function inverse(connector: Connector.And): Connector.Or
      export function inverse(connector: Connector.Or): Connector.And
      export function inverse(connector: Connector): Connector
      export function inverse(connector: Connector): Connector {
        return connector === Connector.And ? Connector.Or : Connector.And
      }
    }
  }

  /**
   * A standard default {@link Filter}
   */
  export const DEFAULT: Filter = {
    connector: Statement.Connector.And,
    filters: [],
  }
}
